import { FilterTable } from "@/features/FilterTable/FilterTable";
import { Field } from "@/features/FilterTable/types";
import {
  dateSchema,
  parseToDateValue,
  preprocessTextAsyncAutocompleteField,
} from "@/features/FormCard/utils";
import { z } from "zod";
import { Clients } from "@/lib/clients";
import { ClientTables } from "@/lib/clientTables";
import { useTranslation } from "react-i18next";

const filterC5DataUploadSchema = z.object({
  file_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  provider_config_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  created_at_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  created_at_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
});
const filterC5DataUploadFields = filterC5DataUploadSchema.keyof().enum;

export const ConfigSchema = z.object({
  id: z.number(),
  code: z.string(),
  file_pattern: z.string().optional(),
  client: z.string().optional(),
  mime_type: z.string().optional(),
  template_s3_key: z.string().optional(),
  usage_description: z.string().optional(),
});

export const UploadSchema = z.object({
  config_name: z.string().min(1, "La configuration est requise"),
  files: z
    .array(z.instanceof(File))
    .nonempty("Vous devez ajouter au moins un fichier"),
});

export type UploadSchemaType = z.infer<typeof UploadSchema>;
export type ConfigSchemaType = z.infer<typeof ConfigSchema>;

export type FilterC5DataUploadSchema = z.infer<typeof filterC5DataUploadSchema>;

export function C5DataUploadFilter() {
  const { t } = useTranslation("tables");

  const fields: Field[] = [
    {
      name: filterC5DataUploadFields.file_name_contains,
      label: t(
        `${ClientTables.C5_DATA_UPLOAD}.filter.${filterC5DataUploadFields.file_name_contains}`,
      ),
      type: "asyncAutocomplete",
      base_url: `${Clients.C5}/${ClientTables.C5_DATA_UPLOAD}`,
      column_name: "file_file_name",
    },
    {
      name: filterC5DataUploadFields.provider_config_name_contains,
      label: t(
        `${ClientTables.C5_DATA_UPLOAD}.filter.${filterC5DataUploadFields.provider_config_name_contains}`,
      ),
      type: "asyncAutocomplete",
      base_url: `${Clients.C5}/${ClientTables.C5_DATA_UPLOAD}`,
      column_name: "provider_config_name",
    },
    {
      name: "created_at",
      type: "dateRange",
      label: t(`${ClientTables.C5_DATA_UPLOAD}.filter.created_at`),
      from: { name: filterC5DataUploadFields.created_at_from },
      to: { name: filterC5DataUploadFields.created_at_to },
      col_span: 2,
    },
  ];

  return (
    <FilterTable
      client={Clients.C5}
      schema={filterC5DataUploadSchema}
      tableName={ClientTables.C5_DATA_UPLOAD}
      fields={fields}
    />
  );
}
